import { safeJsonLd } from "../../lib/safeJsonLd";
import React, { useState } from "react";
import { HelpCircle, Plus, Minus } from "lucide-react";

// AEO-optimized "People Also Ask" section
// Each Q&A is structured for Google AI Overviews + ChatGPT + Claude citation
const peopleAlsoAsk = [
  {
    id: "paa-1",
    question: "Why was my Instagram account disabled?",
    answer: "Instagram typically disables accounts for violating Community Guidelines, suspicious activity suggesting a hacked account, impersonation reports, intellectual property complaints, age verification issues, or automated behavior detection. The specific reason is usually stated in the notification email or in-app message. Each cause has a different appeal path through Instagram's official help center.",
  },
  {
    id: "paa-2",
    question: "Can I appeal a permanently banned account?",
    answer: "Yes, even accounts labeled 'permanently disabled' or 'permanently banned' can be appealed through the platform's official appeal forms. Outcomes depend on the violation type, your account history, and the quality of your documentation. There are no guarantees — platforms alone decide — but a well-prepared, evidence-backed appeal typically has a stronger chance than a generic one.",
  },
  {
    id: "paa-3",
    question: "How do I appeal my Instagram or Facebook account?",
    answer: "1) Locate the suspension notification and note the exact policy cited. 2) Gather evidence: ID verification, business registration (if applicable), original content proofs. 3) Submit through the official Meta appeal form linked in the notification email. 4) Address the specific violation in clear, factual language — avoid emotional or accusatory framing. 5) Wait 10-60 days for review. Social Dispute Co prepares this entire documentation package for you and submits it through the official public appeal channel on your authorization.",
  },
  {
    id: "paa-4",
    question: "Is there a service that helps with social media appeal documentation?",
    answer: "Yes. Social Dispute Co (8eight8 LLC) is an independent service that prepares structured appeal documentation for suspended Instagram, Facebook, TikTok, YouTube, and X accounts. The service reviews your case, organizes your evidence, addresses the specific policy violation, and submits through the platform's official public appeal channel with your authorization. Pricing ranges from $99 (Case Review) to $499 (Priority Support). The service does not guarantee outcomes — platform decisions are always final.",
  },
  {
    id: "paa-5",
    question: "How long does a social media appeal take?",
    answer: "Platform response times vary widely. Instagram and Facebook typically acknowledge appeals within 10 business days and issue decisions in 14-60 days. TikTok appeals range from 3 to 30 days. YouTube appeals can take 2-21 days. X appeals are usually 7-14 days. Social Dispute Co prepares documentation in 3-7 business days (24 hours for Priority Support), but the platform's own review timeline is outside our control.",
  },
  {
    id: "paa-6",
    question: "Are social media appeal services legitimate or are they scams?",
    answer: "Legitimate services are transparent about what they can and cannot do: they prepare documentation and submit through official channels, but cannot guarantee outcomes. Warning signs of scams include: guaranteed restoration claims, requests for your password or login credentials, promises of insider contacts at platforms, and prices that seem too good to be true. Social Dispute Co is an 8eight8 LLC registered business with a public refund policy, Stripe payment processing, and a published '$200 Denial Credit' if the platform issues a final denial.",
  },
];

export const PeopleAlsoAsk = () => {
  const [openId, setOpenId] = useState(null);

  // QAPage JSON-LD — Google recommends single FAQPage per URL, so PAA uses QAPage
  // to avoid conflict with the main FAQ section's FAQPage schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "QAPage",
    "mainEntity": peopleAlsoAsk.map((q) => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": { "@type": "Answer", "text": q.answer },
    })),
  };

  return (
    <section id="people-also-ask" className="py-20">
      <div className="mx-auto max-w-4xl px-6">
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
          dangerouslySetInnerHTML={{ __html: safeJsonLd(faqSchema) }}
        />

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-xs text-blue-400 uppercase tracking-wide mb-4">
            <HelpCircle className="w-3.5 h-3.5" />
            People Also Ask
          </div>
          <h2 className="text-3xl md:text-4xl font-bold">
            Questions We <span className="text-blue-400">Hear Every Day</span>
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl mx-auto">
            Common searches about social media account suspensions, appeals, and what we do.
          </p>
        </div>

        <div className="space-y-3">
          {peopleAlsoAsk.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className={`rounded-2xl border transition-all ${
                  isOpen ? "border-blue-500/40 bg-blue-500/5" : "border-white/10 bg-white/5"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                  className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left"
                  data-testid={`paa-toggle-${item.id}`}
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-base md:text-lg text-white pr-4">
                    {item.question}
                  </span>
                  {isOpen ? (
                    <Minus className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  ) : (
                    <Plus className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 text-slate-300 leading-relaxed text-sm md:text-base">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
